import { getCodeRepoPackagesData } from "@/api/useInventory.selectors"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { isAiDiscoveryEnabled, isAiDiscoveryLive } from "@/domain/user"
import {
  ConfigInfinityQuery,
  predicateHelpers,
  useDelete,
  useHttp,
  useLoadMore,
  useUpdate,
  type QueryClient
} from "@/hooks/useHttp"

import type { Config, Params } from "@/hooks/useHttp"
import { useFeatureFlags } from "@/hooks/useUser"
import type { Module } from "@/molecules/ModulesTree/ModulesTree.types"
import type { Configs } from "@/organisms/MonorepoConfigurations/MonorepoConfigurations.types"
import type {
  AgentAiResponse,
  AgentsAiResponse,
  ApplicationParams,
  ApplicationResponse,
  ArchivedCountsResponse,
  BaseImageDetailsParams,
  BranchConfigRecord,
  BranchResponse,
  CodeRepoPackagesParams,
  CodeRepoPackagesResponse,
  ContainerBaseImageDetailsResponse,
  ContainerBaseImageResponse,
  CorrelationSource,
  CountsResponse,
  EntitiesAi,
  EntityByTypeAndId,
  EnvironmentResponse,
  ImageResponse,
  InventoryCodeRepoPackagesParams,
  InventoryFiltersResponse,
  PackageCommonProps,
  PackageDescribeResponse,
  PackageResponse,
  PackageVulnerabilitiesResponse,
  RepositoryConfigResponse,
  RepositoryCorrelation,
  RepositoryModulesResponse,
  RuntimeCoverageResponse,
  SnapshotResponse,
  StatisticsAiResponse,
  ToolInvocationsParams,
  ToolInvocationsResponse,
  TransitiveFixesResponse,
  UpdateRepositoryConfigRequestBody,
  VMResponse
} from "./useInventory.types"
export type { Snapshot } from "./useInventory.types"

export const useGetInventorySnapshot = <T = SnapshotResponse, S = T>(
  params: Params = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INVENTORIES.SNAPSHOT,
    params,
    config
  )

  return context
}

export const useGetRuntimeCoverage = <T = RuntimeCoverageResponse, S = T>(
  params: Params = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INVENTORIES.RUNTIME_COVERAGE,
    params,
    config
  )

  return context
}

export const useGetInventoryEnvironment = <T = EnvironmentResponse, S = T>(
  params: Params = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INVENTORIES.ENVIRONMENT,
    params,
    config
  )

  return context
}

export const useGetInventoryEnvironmentOptions = () => {
  const { response, isLoading } = useGetInventoryEnvironment()
  const environmentOptions =
    response?.data?.map((env) => ({
      label: env.displayName,
      value: env.displayName
    })) || []

  return { environmentOptions, isLoading }
}

export const useGetInventoryApplication = <T = ApplicationResponse, S = T>(
  params: Params<ApplicationParams> = { pageNumber: "0" },
  config?: Config<T, S>
) => {
  const { needle = "", ...restParams } = params
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INVENTORIES.APPLICATION}${needle}`,
    restParams,
    config
  )

  return context
}

export const useGetApplicationToolInvocations = <
  T = ToolInvocationsResponse,
  S = T
>(
  applicationId: string | undefined,
  params?: Params<ToolInvocationsParams>,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INVENTORIES.APPLICATION_TOOL_INVOCATIONS(
      applicationId || ""
    ),
    params,
    { ...config, enabled: !!applicationId && config?.enabled !== false }
  )

  return context
}

export const useGetInventoryImage = <T = ImageResponse, S = T>(
  params: Params<ApplicationParams> = { pageNumber: "0" },
  config?: Config<T, S>
) => {
  const { needle = "", ...restParams } = params

  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INVENTORIES.IMAGES}${needle}`,
    restParams,
    config
  )

  return context
}

export const useGetInventoryBranch = <T = BranchResponse, S = T>(
  params: Params<ApplicationParams> = { pageNumber: "0" },
  config?: Config<T, S>
) => {
  const { needle = "", ...restParams } = params

  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INVENTORIES.BRANCH}${needle}`,
    restParams,
    config
  )

  return context
}

export const useGetInventoryContainerBaseImage = <
  T = ContainerBaseImageResponse,
  S = T
>(
  params: Params<ApplicationParams> = { pageNumber: "0" },
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INVENTORIES.CONTAINER_BASE_IMAGE,
    params,
    config
  )

  return context
}

export const useGetInventoryContainerBaseImageDetails = <
  T = ContainerBaseImageDetailsResponse,
  S = T
>(
  baseImageDigest: string,
  params?: Params<BaseImageDetailsParams>,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INVENTORIES.CONTAINER_BASE_IMAGE}/${baseImageDigest}`,
    params,
    config
  )

  return context
}

export const useGetInventoryCounts = <T = CountsResponse, S = T>(
  params: Params = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INVENTORIES.COUNTS,
    params,
    config
  )

  return context
}

export const useGetInventoryCodeRepoPackages = <
  T extends CodeRepoPackagesResponse = CodeRepoPackagesResponse,
  S = ReturnType<typeof getCodeRepoPackagesData>
>(
  repoName: string,
  params: InventoryCodeRepoPackagesParams = {},
  config: Config<T, S> = {}
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INVENTORIES.BRANCH}/${repoName}`,
    params,
    config
  )

  return context
}

export const useGetTransitiveFixesCodeRepository = <
  T extends TransitiveFixesResponse = TransitiveFixesResponse,
  S = T
>(
  repoName: string,
  params: Params<CodeRepoPackagesParams> = {},
  config: Config<T, S> = {}
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INVENTORIES.BRANCH}/${repoName}`,
    params,
    config
  )

  return context
}

export const useGetItemByIdInventory = <T, S = T>(
  { id, type }: EntityByTypeAndId,
  params?: Params,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INVENTORY}/${type}/${id}`,
    params,
    config
  )

  return context
}

export const useGetArchivedRepositoriesCount = <
  T = ArchivedCountsResponse,
  S = T
>() => {
  const context = useHttp<T, S>(RESOURCES_NAMES.INVENTORIES.ARCHIVED)

  return context
}

export const useGetPackages = <T = PackageResponse, S = T>(
  params: Params,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INVENTORIES.PACKAGE,
    params,
    config
  )

  return context
}

export const useLoadMoreProjectsByPackage = <T = any>(
  params: Params<{ needle: string; pageNumber?: number | string }>,
  config: ConfigInfinityQuery<T>
) => {
  const { needle = "", pageNumber, ...restParams } = params

  const context = useLoadMore<T>(
    `${RESOURCES_NAMES.INVENTORIES.PACKAGE}${needle}`,
    restParams,
    config
  )

  return context
}

export const useGetPackageVulnerabilities = <
  T = PackageVulnerabilitiesResponse,
  S = T
>(
  params: PackageCommonProps,
  config: Config<T, S>
) => {
  const { projectId, id, ...rest } = params
  const context = useHttp(
    `${RESOURCES_NAMES.INVENTORIES.PACKAGE}/${projectId}/${id}/vulnerabilities`,
    rest,
    config
  )

  return context
}

export const useGetPackageDescribe = <T = PackageDescribeResponse, S = T>(
  params: PackageCommonProps,
  config: Config<T, S>
) => {
  const { projectId, id, ...rest } = params
  const context = useHttp(
    `${RESOURCES_NAMES.INVENTORIES.PACKAGE}/${projectId}/${id}/describe`,
    rest,
    config
  )

  return context
}

export const useGetInventoryVirtualMachine = <T = VMResponse, S = T>(
  params: Params<ApplicationParams & { pageSize?: string }> = {
    pageNumber: "0"
  },
  config?: Config<T, S>
) => {
  const { needle = "", ...restParams } = params
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.INVENTORIES.VM}${needle}`,
    restParams,
    config
  )

  return context
}

export const useGetRepositoryModules = (
  repositoryName: string,
  record?: BranchConfigRecord
) => {
  const context = useHttp<RepositoryModulesResponse, Module[]>(
    RESOURCES_NAMES.INVENTORIES.BRANCH_MODULES(repositoryName, record),
    {},
    {
      enabled: !!repositoryName && !!record?.codeRepoId,
      select(data) {
        if (!data || !data.data || data.data.length === 0) return []

        const modules = data.data.flatMap(({ path, files }) =>
          files.map((file) => {
            const moduleInfo = {
              name: file.name,
              path: `${path}/${file.name}`,
              type: "manifest"
            }

            // Remove undefined properties
            return Object.fromEntries(
              Object.entries(moduleInfo).filter(([_, v]) => v !== undefined)
            ) as Module
          })
        )
        return modules
      }
    }
  )
  return context
}

const optionsRepositoryConfig = (queryClient: QueryClient) => ({
  onSuccess: () =>
    queryClient.invalidateQueries({
      predicate: (query) =>
        predicateHelpers.includesKeysPredicate([
          `${RESOURCES_NAMES.INVENTORIES.BRANCH}`
        ])(query) ||
        predicateHelpers.includesKeysPredicate([
          RESOURCES_NAMES.INVENTORIES.VM
        ])(query)
    })
})

export const useUpdateRepositoryConfig = (
  repositoryName: string,
  record?: BranchConfigRecord
) => {
  const mutation = useUpdate<
    UpdateRepositoryConfigRequestBody,
    UpdateRepositoryConfigRequestBody
  >(
    RESOURCES_NAMES.INVENTORIES.BRANCH_CONFIG(repositoryName, record),
    undefined,
    undefined,
    optionsRepositoryConfig
  )
  return mutation
}

const removeLeadingSlash = (path: string) => path.replace(/^\//, "")

export const getCorrelationVM = (
  vmCorrelations: RepositoryConfigResponse["data"]["vmCorrelations"]
) =>
  (vmCorrelations || []).reduce(
    (acc, vm) => {
      acc[vm.repoPath] = vm

      return acc
    },
    {
      __isRoot: vmCorrelations?.find(
        (vm) => vm.repoPath === "" || vm.repoPath === "/"
      )
    } as Record<
      string,
      RepositoryConfigResponse["data"]["vmCorrelations"][number]
    >
  )

export const useGetRepositoryConfig = (
  repositoryName: string,
  record?: BranchConfigRecord
) => {
  const context = useHttp<
    RepositoryConfigResponse,
    {
      config: Configs
      correlations: RepositoryCorrelation[]
      repoPathsVM: Record<
        string,
        RepositoryConfigResponse["data"]["vmCorrelations"][number]
      >
    }
  >(
    RESOURCES_NAMES.INVENTORIES.BRANCH_CONFIG(repositoryName, record),
    {},
    {
      enabled: !!repositoryName && !!record?.codeRepoId,
      select(data) {
        if (!data || !data.data)
          return {
            config: {},
            correlations: [],
            repoPathsVM: {}
          }
        const config = data.data.config
        const correlations = data.data.imageCorrelations || []
        const correlationSource = correlations.reduce(
          (acc, correlation) => ({
            ...acc,
            [correlation.imageRepository]: correlation.source
          }),
          {}
        ) as Record<string, CorrelationSource>
        if (!config)
          return {
            correlations,
            config: {},
            repoPathsVM: getCorrelationVM(data.data.vmCorrelations)
          }
        const formattedConfigurations = config.projects.reduce(
          (acc, project) => {
            for (const path of project.paths) {
              acc[removeLeadingSlash(path)] = {
                id: project.id,
                projectName: project.name,
                splitPath: project.split,
                exclude: !!project.ignore,
                imageRepositories: project.imageRepositories.map(
                  (imageRepository) => ({
                    imageRepository,
                    correlationType:
                      correlationSource[imageRepository] || "manual"
                  })
                )
              }
            }
            return acc
          },
          {} as Configs
        )
        return {
          config: formattedConfigurations,
          correlations,
          repoPathsVM: getCorrelationVM(data.data.vmCorrelations)
        }
      }
    }
  )
  return context
}

export const useResetRepositoryConfig = (
  repositoryName: string,
  record?: BranchConfigRecord
) => {
  const mutation = useDelete<undefined, undefined>(
    RESOURCES_NAMES.INVENTORIES.BRANCH_CONFIG(repositoryName, record),
    undefined,
    undefined,
    optionsRepositoryConfig
  )
  return mutation
}

export const useGetInventoryPackageFilters = <
  T = InventoryFiltersResponse,
  S = T
>(
  params: Params = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.INVENTORIES.PACKAGE_FILTERS,
    params,
    config
  )

  return context
}

export const useAiAPI = <T, S = T>(config: Config<T, S> | undefined) => {
  const aiDiscovery = useFeatureFlags("aiDiscovery")
  const { EXPERIMENTAL, INVENTORIES } = RESOURCES_NAMES

  return {
    enabled: isAiDiscoveryEnabled(aiDiscovery) && config?.enabled !== false,
    prefix: isAiDiscoveryLive(aiDiscovery)
      ? INVENTORIES.AI_DISCOVERY
      : EXPERIMENTAL.AI_DISCOVERY
  }
}

export const useStatisticsAI = <T = StatisticsAiResponse, S = T>(
  config?: Config<T, S>
) => {
  const { prefix, enabled } = useAiAPI(config)
  const context = useHttp<T, S>(`${prefix}/statistics`, undefined, {
    ...config,
    enabled
  })

  return context
}

export const useEntitiesAI = <T = AgentsAiResponse, S = T>(
  type: EntitiesAi,
  params?: Params,
  config?: Config<T, S>
) => {
  const { prefix, enabled } = useAiAPI(config)
  const context = useHttp<T, S>(`${prefix}/${type}`, params, {
    ...config,
    enabled
  })

  return context
}

export const useEntityAI = <T = AgentAiResponse, S = T>(
  needle: string | undefined,
  type: EntitiesAi,
  params?: Params,
  config?: Config<T, S>
) => {
  const { prefix, enabled } = useAiAPI(config)
  const context = useHttp<T, S>(`${prefix}/${type}/${needle}`, params, {
    ...config,
    enabled: enabled && !!needle
  })

  return context
}

export const useFiltersAi = <T, S = T>(type: string, config?: Config<T, S>) => {
  const { prefix, enabled } = useAiAPI(config)
  const context = useHttp<T, S>(`${prefix}/${type}/filters`, undefined, {
    ...config,
    enabled
  })

  return context
}
